import { useToast } from '@shared/hooks/use-toast';
import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Location } from '../../models/Location';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { LocationApiAdapter } from '../../adapters/LocationApiAdapter';
import { LocationService } from '../../services/LocationService';
import { getErrorMessage } from '@shared/utils/error.utils';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
}

interface UnlockLocationModalProps {
  onSuccess?: () => void;
}

const UnlockLocationModal = forwardRef<ExtendedModalCenterRef, UnlockLocationModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('locationsModule');
  const { toast } = useToast();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [location, setLocation] = useState<Location | null>(null);

  const resetForm = () => {
    setLocation(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new LocationApiAdapter(httpClient), [httpClient]);
  const service = useMemo(() => new LocationService(apiAdapter), [apiAdapter]);

  const handleUnlock = async () => {
    try {
      if (!location) {
        toast({
          title: t('errors.generic'),
          description: t('modal.unlock.error_no_selection'),
          variant: 'destructive',
        });
        return;
      }

      await service.unlockLocation(location.id);

      toast({
        title: t('modal.unlock.success_title'),
        description: t('modal.unlock.success_description'),
        variant: 'default',
      });

      resetForm();
      handleCancel();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: t('modal.unlock.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithLocation = useCallback((locationData: Location) => {
    setLocation(locationData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithLocation, // ← Método nuevo para uso directo
    }),
    [openWithLocation],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modal.unlock.title')} type="success">
      <div className="space-y-4">
        <p className="text-sm text-text">{t('modal.unlock.description')}</p>
        <p className="text-sm text-text font-medium">
          {t('modal.unlock.confirmation')} <strong>{location?.code}</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="lg" onClick={handleCancel} name="cancel" className="w-full sm:w-auto">
            {t('button.cancel')}
            <EscIcon className="ml-1 !h-4 !w-auto" />
          </Button>
          <Button
            variant="primary"
            size="lg"
            name="unlock-btn-confirm"
            onClick={handleUnlock}
            className="w-full sm:w-auto"
          >
            {t('button.unlock')}
            <EnterIcon className="ml-1 !h-3.5 !w-auto" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default UnlockLocationModal;
UnlockLocationModal.displayName = 'UnlockLocationModal';
